import _ from 'lodash';
import textActionTypes from './textActionTypes';
import * as selection from '../selection/selection';
import * as components from '../components/components';
import {
  getSelectedComponent,
  getSelectedColor,
  getWidgetDesignSelectedComponents,
} from './textSelectors';
import constants from '#packages/constants';
import { mobileUtils } from '#packages/util';
import type { DSRead } from 'types/documentServices';
import type { CompRef, CompData } from '@wix/document-services-types';
import type { CmdState, TextManager } from '@wix/wix-richtext';
import type { EditorAPI } from '#packages/editorAPI';
import type { Dispatch, StateMapperArgs } from 'types/redux';
import type { EditorState } from '#packages/stateManagement';
import type { NonThemedAction, ThemedAction } from '#packages/textControls';

const {
  OPEN_TEXT_EDITOR,
  CLOSE_TEXT_EDITOR,
  SET_SELECTED_COMPONENT,
  SET_SELECTED_COLOR,
  LOAD_FROM_WIDGET_DESIGN,
  CLEAR_WIDGET_DESIGN_STATE,
  SET_FONT_FAMILY_DROPDOWN_OPENED,
} = textActionTypes;

export interface UndoRedoStack {
  commandName: string;
  params: any;
}

const undoStack: UndoRedoStack[] = [];
const redoStack: UndoRedoStack[] = [];
const openTextEditor = (
  editStyleOnly = false,
  isLinkPanelOpen = false,
  origin: string | null = null,
) => {
  return {
    type: OPEN_TEXT_EDITOR,
    editStyleOnly,
    isLinkPanelOpen,
    origin,
  };
};

const closeTextEditor = () => ({
  type: CLOSE_TEXT_EDITOR,
});

const setFontFamilyDropdownOpen = (isOpen: boolean) => ({
  type: SET_FONT_FAMILY_DROPDOWN_OPENED,
  isOpen,
});

const setSelectedComponent = (selectedComponent: CompRef) => ({
  type: SET_SELECTED_COMPONENT,
  selectedComponent,
});

const setSelectedColor = (selectedColor: string) => ({
  type: SET_SELECTED_COLOR,
  selectedColor,
});

const loadFromWidgetDesign = (
  selectedComponents: CompRef[],
  panelProps: AnyFixMe,
) => ({
  type: LOAD_FROM_WIDGET_DESIGN,
  selectedComponents,
  panelProps,
});

const clearWidgetDesignState = () => ({
  type: CLEAR_WIDGET_DESIGN_STATE,
});

const getCommandData = (commandName: string, commandData: AnyFixMe) => ({
  data: { commandName, commandData },
});

const loadInvisibleTextManager =
  (textManager: TextManager) =>
  (dispatch: Dispatch, getState: () => EditorState, services: AnyFixMe) => {
    Object.assign(services, {
      invisibleTextManager: textManager,
    });
  };

const unloadInvisibleTextManager =
  () =>
  (dispatch: Dispatch, getState: () => EditorState, services: AnyFixMe) => {
    Object.assign(services, {
      invisibleTextManager: null,
    });
  };

const loadInvisibleLinksHelper =
  (linkHelper: AnyFixMe) =>
  (dispatch: Dispatch, getState: () => EditorState, services: AnyFixMe) => {
    Object.assign(services, {
      invisibleLinksHelper: linkHelper,
    });
  };

const unloadInvisibleLinksHelper =
  () =>
  (dispatch: Dispatch, getState: () => EditorState, services: AnyFixMe) => {
    Object.assign(services, {
      invisibleLinksHelper: null,
    });
  };

const loadTextManager =
  (textManager: TextManager) =>
  (dispatch: Dispatch, getState: () => EditorState, services: AnyFixMe) => {
    Object.assign(services, {
      textManager,
    });
  };

const unloadTextManager =
  () =>
  (dispatch: Dispatch, getState: () => EditorState, services: AnyFixMe) => {
    Object.assign(services, {
      textManager: null,
    });
  };

const statefulCommands = new Set(['bold', 'italic', 'underline']);

const ckUpdateData = (
  editorAPI: EditorAPI,
  invisibleTextManager: TextManager,
  invisibleLinksHelper: AnyFixMe,
  compRef: CompRef,
  data: CompData,
) => {
  // get new Data and update
  data.text = invisibleTextManager.getData();
  data.linkList = invisibleLinksHelper.getAllLinks();
  editorAPI.components.data.update(compRef, data);
};

const getComponentsToEditData = (
  remainingComponents: CompRef[],
  dsRead: DSRead,
) => {
  const textComponentToEditData = components.selectors.getCompsData(
    remainingComponents,
    dsRead,
  );

  return textComponentToEditData;
};

const execInvisibleCommandForRepeaterComp = (
  event: AnyFixMe,
  selectedComponent: CompRef,
  invisibleTextManager: TextManager,
  invisibleLinksHelper: AnyFixMe,
  dsRead: DSRead,
  editorAPI: EditorAPI,
  cmdOptions?: any,
) => {
  const isTextComp =
    components.selectors.getCompType(selectedComponent, dsRead) ===
    constants.COMP_TYPES.TEXT;
  const repeatedComponents =
    dsRead.deprecatedOldBadPerformanceApis.components.getRepeatedComponents(
      selectedComponent,
    );
  if (isTextComp && repeatedComponents.length > 1) {
    const textComponentToEditData = getComponentsToEditData(
      repeatedComponents,
      dsRead,
    );

    textComponentToEditData.forEach((data, dataIndex) => {
      const { commandName, commandData } = event.data;
      const compRef = repeatedComponents[dataIndex];

      // For mobile-only components, the fontSize value comes from the mobile UI
      // (mobile space) but the invisible editor works with raw desktop data.
      // Convert the value to desktop space so it's stored correctly.
      let effectiveCommandData = commandData;
      const numericFontSize = parseFloat(commandData);
      if (
        commandName === 'fontSize' &&
        !isNaN(numericFontSize) &&
        editorAPI.mobile.mobileOnlyComponents.isMobileOnlyNonNativeComponent(
          compRef,
        )
      ) {
        const scale =
          editorAPI.components.layout.getRelativeToScreen(compRef)?.scale || 1;
        const desktopSize = mobileUtils.getSourceFontSizeFromMobileFontSize(
          numericFontSize,
          scale,
        );
        effectiveCommandData = `${desktopSize}px`;
      }

      // set CK with comp data
      invisibleTextManager.setData(data.text, {
        validateHtml: false,
        removeNestedStyles: true,
      });
      invisibleLinksHelper.setCurrentLinks(data.linkList);

      if (statefulCommands.has(commandName)) {
        const commandStates = invisibleTextManager.getCommandsStates();
        const isAlreadyInDesiredState =
          commandData &&
          commandStates[commandName as keyof CmdState] === commandData.value;

        if (isAlreadyInDesiredState) {
          return;
        }
      }

      // select all text and exec command
      invisibleTextManager.execCommand('selectAll', {
        execWithoutFocus: cmdOptions?.execWithoutFocus,
      });
      invisibleTextManager.execCommand(commandName, effectiveCommandData, {
        execWithoutHistory: true,
        execWithoutFocus: true,
      });
      ckUpdateData(
        editorAPI,
        invisibleTextManager,
        invisibleLinksHelper,
        compRef,
        data,
      );
    });
  }
};

const execInvisibleSeoTagChangeForRepeaterComp = (
  editorAPI: EditorAPI,
  tag: string,
  selectedComponent: CompRef,
  invisibleTextManager: TextManager,
  invisibleLinksHelper: AnyFixMe,
  dsRead: DSRead,
) => {
  const isTextComp =
    components.selectors.getCompType(selectedComponent, dsRead) ===
    constants.COMP_TYPES.TEXT;
  const repeatedComponents =
    dsRead.deprecatedOldBadPerformanceApis.components.getRepeatedComponents(
      selectedComponent,
    );
  if (isTextComp && repeatedComponents.length > 1) {
    const textComponentToEditData = getComponentsToEditData(
      repeatedComponents,
      dsRead,
    );

    textComponentToEditData.forEach((data, dataIndex) => {
      // set CK with comp data
      invisibleTextManager.setData(data.text, {
        validateHtml: false,
        removeNestedStyles: true,
      });
      invisibleLinksHelper.setCurrentLinks(data.linkList);

      invisibleTextManager.setSeoTag(tag);
      ckUpdateData(
        editorAPI,
        invisibleTextManager,
        invisibleLinksHelper,
        repeatedComponents[dataIndex],
        data,
      );
    });
  }
};

const execRepeaterSeoTagChange =
  (tag: string) =>
  (
    dispatch: Dispatch,
    getState: () => EditorState,
    {
      invisibleTextManager,
      invisibleLinksHelper,
      dsRead,
      editorAPI,
    }: {
      invisibleTextManager: TextManager;
      invisibleLinksHelper: AnyFixMe;
    } & StateMapperArgs,
  ) => {
    const selectedComponent = _.head(
      selection.selectors.getSelectedCompsRefs(getState()),
    );
    execInvisibleSeoTagChangeForRepeaterComp(
      editorAPI,
      tag,
      selectedComponent,
      invisibleTextManager,
      invisibleLinksHelper,
      dsRead,
    );
  };

const execInvisibleCommand =
  (event: any) =>
  (
    dispatch: Dispatch,
    getState: () => EditorState,
    {
      invisibleTextManager,
      invisibleLinksHelper,
      dsRead,
      editorAPI,
    }: {
      invisibleTextManager: TextManager;
      invisibleLinksHelper: AnyFixMe;
    } & StateMapperArgs,
  ) => {
    const selectedComponent = _.head(
      selection.selectors.getSelectedCompsRefs(getState()),
    );
    if (event.isRepeated) {
      execInvisibleCommandForRepeaterComp(
        event,
        selectedComponent,
        invisibleTextManager,
        invisibleLinksHelper,
        dsRead,
        editorAPI,
      );
    } else {
      execInvisibleCommand_Single(
        event,
        selectedComponent,
        invisibleTextManager,
        invisibleLinksHelper,
        dsRead,
        editorAPI,
      );
    }
  };

const guardedCommandsList = [
  'justifyleft',
  'justifycenter',
  'justifyright',
  'justifyblock',
];

const execInvisibleCommand_Single = (
  event: any,
  compRef: CompRef,
  invisibleTextManager: TextManager,
  invisibleLinksHelper: AnyFixMe,
  dsRead: DSRead,
  editorAPI: EditorAPI,
) => {
  const isTextComp =
    components.selectors.getCompType(compRef, dsRead) ===
    constants.COMP_TYPES.TEXT;

  if (!isTextComp) return;

  const textData = editorAPI.components.data.get(compRef);

  const { commandName, commandData, cmdOptions } = event.data;

  // set CK with comp data
  invisibleTextManager.setData(textData.text, {
    validateHtml: false,
    removeNestedStyles: true,
  });
  invisibleLinksHelper.setCurrentLinks(textData.linkList);

  if (statefulCommands.has(commandName)) {
    const commandStates = invisibleTextManager.getCommandsStates();
    const isAlreadyInDesiredState =
      commandData &&
      commandStates[commandName as keyof CmdState] === commandData.value;

    if (isAlreadyInDesiredState) {
      return;
    }
  }

  // don't execute command on text if it already has those styles applied
  if (
    guardedCommandsList.includes(commandName) &&
    invisibleTextManager.getCommandsStates()[commandName as keyof CmdState]
  )
    return;

  // select all text and exec command
  invisibleTextManager.execCommand('selectAll', {
    execWithoutFocus: cmdOptions?.execWithoutFocus,
  });
  invisibleTextManager.execCommand(commandName, commandData, {
    execWithoutHistory: true,
    execWithoutFocus: true,
  });
  ckUpdateData(
    editorAPI,
    invisibleTextManager,
    invisibleLinksHelper,
    compRef,
    textData,
  );
};

const getRemainingComponents = (getState: () => EditorState) => {
  const widgetDesignSelectedComponents =
    getWidgetDesignSelectedComponents(getState());
  const selectedComponents =
    widgetDesignSelectedComponents?.length > 0
      ? widgetDesignSelectedComponents
      : selection.selectors.getSelectedCompsRefs(getState());
  const selectedTextComp = getSelectedComponent(getState());
  return _.reject(selectedComponents, ['id', selectedTextComp.id]);
};

const execMultiCompCommands =
  (commandName: string, params: any, cmdOptions: any, isMouseOut: boolean) =>
  (
    dispatch: Dispatch,
    getState: () => EditorState,
    {
      invisibleTextManager,
      invisibleLinksHelper,
      textManager,
      editorAPI,
      dsRead,
    }: {
      textManager: TextManager;
      invisibleTextManager: TextManager;
      invisibleLinksHelper: AnyFixMe;
    } & StateMapperArgs,
  ) => {
    undoStack.push({
      commandName,
      params: textManager.getCommandsStates()[commandName as keyof CmdState],
    });
    if (isMouseOut) {
      params = getSelectedColor(getState());
    }

    const remainingComponents = getRemainingComponents(getState);
    remainingComponents.forEach((compRef) => {
      const data = editorAPI.components.data.get(compRef);

      // set CK with comp data
      invisibleTextManager.setData(data.text, {
        validateHtml: false,
        removeNestedStyles: true,
      });
      invisibleLinksHelper.setCurrentLinks(data.linkList);

      const invisibleCommandsState = invisibleTextManager.getCommandsStates();
      const commandsState = textManager.getCommandsStates();
      const shouldExecuteCommand = _.isBoolean(
        invisibleCommandsState[commandName as keyof CmdState],
      )
        ? invisibleCommandsState[commandName as keyof CmdState] ===
          commandsState[commandName as keyof CmdState]
        : true;

      if (shouldExecuteCommand) {
        invisibleTextManager.execCommand('selectAll', {
          execWithoutFocus: cmdOptions?.execWithoutFocus,
        });
        invisibleTextManager.execCommand(commandName, params, {
          execWithoutHistory: true,
          execWithoutFocus: cmdOptions?.execWithoutFocus,
        });
        ckUpdateData(
          editorAPI,
          invisibleTextManager,
          invisibleLinksHelper,
          compRef,
          data,
        );
        execInvisibleCommandForRepeaterComp(
          getCommandData(commandName, params),
          compRef,
          invisibleTextManager,
          invisibleLinksHelper,
          dsRead,
          editorAPI,
          cmdOptions,
        );
      }
    });
    textManager.execCommand(commandName, params, cmdOptions);
    if (!cmdOptions?.execWithoutFocus) {
      textManager.focus();
    }
  };

const execSeoTagChange =
  (tagName: string) =>
  (
    dispatch: Dispatch,
    getState: () => EditorState,
    {
      invisibleTextManager,
      invisibleLinksHelper,
      textManager,
      editorAPI,
      dsRead,
    }: {
      textManager: TextManager;
      invisibleTextManager: TextManager;
      invisibleLinksHelper: AnyFixMe;
    } & StateMapperArgs,
  ) => {
    const remainingComponents = getRemainingComponents(getState);
    remainingComponents.forEach((compRef) => {
      const data = editorAPI.components.data.get(compRef);
      invisibleTextManager.setData(data.text, {
        validateHtml: false,
        removeNestedStyles: true,
      });
      invisibleLinksHelper.setCurrentLinks(data.linkList);
      invisibleTextManager.setSeoTag(tagName);
      ckUpdateData(
        editorAPI,
        invisibleTextManager,
        invisibleLinksHelper,
        compRef,
        data,
      );
      execInvisibleSeoTagChangeForRepeaterComp(
        editorAPI,
        tagName,
        compRef,
        invisibleTextManager,
        invisibleLinksHelper,
        dsRead,
      );
    });

    textManager.setSeoTag(tagName);
  };

const execStylePropertyMultiCompCommands =
  (
    action: ThemedAction<any> | NonThemedAction<any>,
    value: any,
    theme: any,
    textControls: any,
  ) =>
  (
    dispatch: Dispatch,
    getState: () => EditorState,
    { editorAPI }: StateMapperArgs,
  ) => {
    const componentRefs = getWidgetDesignSelectedComponents(getState());
    componentRefs.forEach((compRef) => {
      const compStyle = editorAPI.components.style.get(compRef);

      if (action.type === 'themed') {
        textControls.themedActions.applyThemedAction(
          compRef,
          action,
          value,
          compStyle,
          theme,
          editorAPI.components.style.update,
        );
        return;
      }

      textControls.nonThemedAction.applyNonThemedAction(
        compRef,
        action,
        value,
        compStyle,
        editorAPI.components.style.update,
      );
    });
  };

const handleMultiselectUndoRedo = ({
  primaryStack,
  secondaryStack,
  getState,
  editorAPI,
  invisibleTextManager,
  invisibleLinksHelper,
  dsRead,
}: {
  primaryStack: UndoRedoStack[];
  secondaryStack: UndoRedoStack[];
  getState: () => EditorState;
  editorAPI: EditorAPI;
  invisibleTextManager: TextManager;
  invisibleLinksHelper: AnyFixMe;
  textManager: TextManager;
  dsRead: DSRead;
}) => {
  const remainingComponents = getRemainingComponents(getState);
  if (!_.isEmpty(primaryStack)) {
    const { commandName, params } = primaryStack.pop();
    secondaryStack.push({ commandName, params });
    remainingComponents.forEach((compRef) => {
      const data = editorAPI.components.data.get(compRef);
      invisibleTextManager.setData(data.text, {
        validateHtml: false,
        removeNestedStyles: true,
      });
      invisibleTextManager.execCommand('selectAll');
      invisibleTextManager.execCommand(commandName, params, {
        execWithoutHistory: true,
      });
      data.text = invisibleTextManager.getData();
      editorAPI.components.data.update(compRef, data);
      execInvisibleCommandForRepeaterComp(
        getCommandData(commandName, params),
        compRef,
        invisibleTextManager,
        invisibleLinksHelper,
        dsRead,
        editorAPI,
      );
    });
  }
};

const multiselectUndo =
  () =>
  (
    dispatch: Dispatch,
    getState: () => EditorState,
    {
      invisibleTextManager,
      editorAPI,
      invisibleLinksHelper,
      textManager,
      dsRead,
    }: {
      invisibleTextManager: TextManager;
      textManager: TextManager;
      invisibleLinksHelper: AnyFixMe;
      editorAPI: EditorAPI;
      dsRead: DSRead;
    },
  ) => {
    handleMultiselectUndoRedo({
      primaryStack: undoStack,
      secondaryStack: redoStack,
      getState,
      editorAPI,
      invisibleTextManager,
      invisibleLinksHelper,
      textManager,
      dsRead,
    });
  };

const multiselectRedo =
  () =>
  (
    dispatch: Dispatch,
    getState: () => EditorState,
    {
      invisibleTextManager,
      editorAPI,
      invisibleLinksHelper,
      textManager,
      dsRead,
    }: {
      invisibleTextManager: TextManager;
      textManager: TextManager;
      invisibleLinksHelper: AnyFixMe;
      editorAPI: EditorAPI;
      dsRead: DSRead;
    },
  ) => {
    handleMultiselectUndoRedo({
      primaryStack: redoStack,
      secondaryStack: undoStack,
      getState,
      editorAPI,
      invisibleTextManager,
      invisibleLinksHelper,
      textManager,
      dsRead,
    });
  };

const invisibleTextEditor = {
  load: loadInvisibleTextManager,
  unload: unloadInvisibleTextManager,
  execCmd: execInvisibleCommand,
  changeSeoTag: execRepeaterSeoTagChange,
  loadLinksHelper: loadInvisibleLinksHelper,
  unloadLinksHelper: unloadInvisibleLinksHelper,
};

const textEditor = {
  load: loadTextManager,
  unload: unloadTextManager,
  loadFromWidgetDesign,
  clearWidgetDesignState,
};

const multiselect = {
  execCmd: execMultiCompCommands,
  changeSeoTag: execSeoTagChange,
  execStylePropertyCmd: execStylePropertyMultiCompCommands,
  setSelectedComponent,
  setSelectedColor,
  undo: multiselectUndo,
  redo: multiselectRedo,
};

export {
  invisibleTextEditor,
  textEditor,
  multiselect,
  openTextEditor,
  closeTextEditor,
  setFontFamilyDropdownOpen,
};
